import { useEffect, useMemo, useState } from 'react';

export function NextCheckInPulse(props: { dueAt?: number | null; graceEndsAt?: number | null; awaiting?: boolean }) {
  const { mode, targetDays } = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const dueAt = typeof props.dueAt === 'number' ? props.dueAt : null;
    const graceEndsAt = typeof props.graceEndsAt === 'number' ? props.graceEndsAt : null;

    // Awaiting trusted-contact verification means we're past both dueAt and
    // graceEndsAt — the danger state is more urgent than grace, but the
    // grace-window check below would fall through to 'normal' (gold) without
    // this short-circuit.
    if (props.awaiting) return { mode: 'awaiting' as const, targetDays: 0 };

    // Same unbounded rule as inGrace in App.tsx: red does not expire when the
    // grace window does. graceEndsAt stays the countdown target where it
    // exists; once passed, the day count clamps to 0 below.
    const isGrace = dueAt !== null && now >= dueAt;
    const target = isGrace && graceEndsAt !== null ? graceEndsAt : dueAt;

    if (target === null) return { mode: 'normal' as const, targetDays: 29 };

    const delta = target - now;
    const d = Math.ceil(delta / (24 * 60 * 60));
    return { mode: isGrace ? 'grace' as const : 'normal' as const, targetDays: Math.max(0, d) };
  }, [props.dueAt, props.graceEndsAt, props.awaiting]);

  const [shownDays, setShownDays] = useState(0);

  useEffect(() => {
    if (targetDays <= 0) {
      setShownDays(0);
      return;
    }
    const duration = 4000;
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setShownDays(Math.round(eased * targetDays));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [targetDays]);

  const isDanger = mode === 'grace' || mode === 'awaiting';
  const isAwaiting = mode === 'awaiting';
  const days = `${shownDays}d`;

  return (
    <>
      <style>{`
        .ncp-wrap{position:relative;width:min(520px,100%);height:64px;border-radius:999px;
          border:1px solid rgba(233,233,239,0.12);background:rgba(0,0,0,0.16);overflow:hidden;
          box-shadow: inset 0px -4px 0px rgba(255,255,255,0.08);
        }
        .ncp-fill{position:absolute;left:0;top:0;height:100%;width:0;z-index:1;pointer-events:none;
          background:linear-gradient(90deg, rgba(210,166,103,0.15), rgba(210,166,103,0.65));
          animation: ncp-fill 8s ease-out forwards;
        }
        .ncp-wrap.isGrace .ncp-fill{
          background:linear-gradient(90deg, rgba(210,80,80,0.18), rgba(210,80,80,0.60));
        }
        @keyframes ncp-fill{from{width:0}to{width:100%}}
        .ncp-ekg{position:absolute;left:150px;right:78px;top:50%;transform:translateY(-50%);opacity:0.28;pointer-events:none;z-index:2;}
        .ncp-wrap.isAwaiting .ncp-ekg{left:230px;}
        .ncp-ekg svg{width:100%;height:20px}
        .ncp-ekg path{fill:none;stroke:rgba(233,233,239,0.85);stroke-width:2;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:120;stroke-dashoffset:120;animation: ncp-dash 1.05s linear infinite;}
        .ncp-wrap.isGrace .ncp-ekg path{animation: ncp-dash 0.84s linear infinite;}
        @keyframes ncp-dash{from{stroke-dashoffset:120}to{stroke-dashoffset:-120}}
        .ncp-readout{position:absolute;inset:0;display:flex;align-items:center;justify-content:space-between;padding:0 16px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size:12px;font-weight:900;letter-spacing:0.4px;color:rgba(233,233,239,0.92);z-index:3;
        }
        .ncp-muted{opacity:0.70;font-weight:800}
      `}</style>

      <div className={`ncp-wrap ${isDanger ? 'isGrace' : ''} ${isAwaiting ? 'isAwaiting' : ''}`.trim()} aria-hidden>
        <div className="ncp-fill" />
        <div className="ncp-ekg">
          <svg viewBox="0 0 200 18" preserveAspectRatio="none">
            {isDanger ? (
              <path d="M0,10 L30,10 L38,3 L44,17 L50,10 L62,10 L70,3 L76,17 L82,10 L125,10 L133,3 L139,17 L145,10 L157,10 L165,3 L171,17 L177,10 L200,10" />
            ) : (
              <path d="M0,10 L30,10 L42,10 L50,3 L58,17 L66,10 L90,10 L108,10 L116,5 L124,15 L132,10 L200,10" />
            )}
          </svg>
        </div>
        <div className="ncp-readout">
          <span className="ncp-muted">{isAwaiting ? 'AWAITING VERIFICATION' : isDanger ? 'GRACE ENDS' : 'NEXT CHECK-IN'}</span>
          {!isAwaiting && <span>{days}</span>}
        </div>
      </div>
    </>
  );
}
